import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Home, Bell, MessageCircle, BookMarked } from 'lucide-react';
import { ROZOSTRENI_CLONY, ROZOSTRENI_NAVIGACE } from '../lib/uiTokens';
import Odznak from './ui/Odznak';

/**
 * MobilniLista — plovoucí skleněná lišta u spodní hrany (norma FofrLama OS).
 *
 * Nahradila Palec vpravo dole. Ten uměl jedinou věc — přepnout sekci stránky —
 * a všechno ostatní (domů, zprávy, notifikace, moduly) viselo na profilovce
 * v pravém horním rohu, kam palec jednou rukou nedosáhne. Lišta dává pět cílů
 * do pásu nad spodní hranou, tedy tam, kde palec je.
 *
 *     [Domů] [Notifikace] ( aktuální modul ) [Zprávy] [Znalosti]
 *
 * **Poslední slot jsou Znalosti, pro všechny stejně** (od 3.489.0). Dřív tu
 * byly Moduly, a Profil u toho, kdo žádný modul neměl. Obsluha moduly mít
 * nemá — návody a jednobodovky ale potřebuje každý. Profil zůstal sekcí
 * Přehledu (střed na Domů). Slot otevře jednoduchý seznam (`ZnalostiSheet`)
 * s články k obrazovce, na které člověk je — ne modul Znalosti, ten je
 * pro administrátory pod Moduly.
 *
 * **Moduly se otevírají dlouhým stiskem středu** (od 2026-10-04; dřív přejetím
 * po liště nahoru a úchytem nad ní — tah se pletl s listováním stránky a úchyt
 * nikdo nehledal). Krátké klepnutí na střed dělá, co dřív: vysune sekce.
 * Jen pro toho, kdo nějaký modul má; ostatním dlouhý stisk nic nedělá.
 *
 * **Zvýraznění aktivního slotu jezdí** (jako lišta Facebooku): jedna kapsle za
 * sloty, která se při změně stránky přesune pod nový slot, a aktivní ikona se
 * vyplní a lehce poskočí. Dřív měl každý slot vlastní pozadí, které jen
 * přeblikl — nebylo poznat, odkud kam se šlo.
 *
 * Střed říká **kde jsem**, sloty kolem něj vedou tam, kam obsluha za směnu
 * chodí nejčastěji. Notifikace i Zprávy bydlí jinde (sekce Přehledu, modul
 * v Týmu), ale slot mají: lišta jsou **zkratky, ne taxonomie**, a co bliká,
 * má být na jedno klepnutí.
 *
 * **Slot Akce tu byl a je pryč.** Nesl tři zkratky do modulů — nahrát doklad,
 * napsat zprávu, nový podnět — tedy totéž co slot Moduly, jen jinou cestou,
 * a popisky slibovaly akci, zatímco otevíraly seznam. Kontext useRychleAkce,
 * kterým si měla otevřená stránka hlásit vlastní akce, nezavolala za celou
 * dobu ani jedna stránka, takže zmizel s ním. Na jeho místě jsou Zprávy: to, co obsluha otevírá
 * nejčastěji a co bylo schované v mřížce modulů na tři klepnutí, s počtem
 * utopeným v součtu odznaku Modulů.
 *
 * **Střed** ukazuje, kde jsem, a nese gesto po Palci: podržet a přejet nahoru
 * vysune sekce stránky, puštěním se sekce přepne. Klepnutí sloupec otevře a
 * nechá otevřený. Stránka sekce hlásí přes `useSekce`; bez nich je střed jen
 * ukazatel a gesto se nechytá.
 *
 * **Střed je kolečko, ne kapsle s názvem.** Pevných 104 px pro název sekce
 * bralo místo čtyřem slotům kolem: na 360px displeji zbylo na popisek 50 px,
 * takže z „Notifikace" bylo `Notifikac…`. Kolečko má 52 px a rozdíl dostanou
 * sloty — po 67 px, kam se popisek vejde. Aby se se jménem neztratilo i to,
 * **kde jsem**, nese kolečko ikonu *aktivní sekce* (Suroviny, Produkty…),
 * ne ikonu modulu; název se ukáže ve sloupci, který střed vysune. Bez sekcí
 * zůstává ikona modulu.
 *
 * **Stav nese slot, na kterém se zůstává.** Domů, Notifikace i Zprávy jsou
 * cíle, kde člověk tráví čas, takže když na nich stojí, slot to ukáže.
 * Notifikace se dřív nezvýrazňovaly — braly se jako zkratka, která otevře
 * sekci Přehledu a jde se dál. Od té doby mají vlastní stránku (Aktivita)
 * a v liště pak nesvítilo nic: Domů schválně zhasne (je to sice Přehled, ale
 * člověk je v notifikacích) a nic ho nenahradilo.
 *
 * **Zvýrazňuje plocha, ne barva textu.** Obarvit popisek brandovou oranžovou
 * vypadalo dobře na tmavém skle a bylo to k nepřečtení na světlém: oranžová
 * má na mléčném skle kontrast 2,27 : 1, zatímco tlumený popisek vedle 4,58 : 1
 * — zvýraznění text zneviditelnilo. Aktivní slot proto dostane kapsli
 * (`vyzdvih`, stejný materiál jako střed) a text zůstane v plné barvě.
 *
 * **Odznaky nejsou jeden součet.** Dřív bylo jedno číslo na profilovce součtem
 * zpráv, notifikací a odznaků modulů — nedalo se z něj poznat, co vlastně čeká.
 * Tady má svůj počet každý slot zvlášť: notifikace, zprávy a to, co čeká
 * uvnitř modulů.
 *
 * **Při listování dolů se lišta schová, ale vždy se vrátí** — při listování
 * nahoru, po chvilce bez scrollu, při přepnutí stránky či sekce. Návrat
 * nezávisí na prvku, který ji schoval (viz komentář u `useSchovaniPriListovani`).
 *
 * Lišta leží POD celoobrazovkovými vrstvami (menu modulů 70, chat 80, modály
 * 1100) — co si vezme celou obrazovku, vezme si i spodní hranu. Menu bylo
 * chvíli pod ní (z-20) a bralo si zpod ní 86 px rezervy; teď ji překrývá
 * a zavírá se vlastním křížkem.
 */

const VYSKA = 64;
// Odstup od krajů. S pěti sloty musí být malý — kapsle se řídí obsahem a při
// odstupu 26 px se začaly dotýkat. Je to prostá aritmetika: užší pás snese tři
// sloty, ne pět.
const ODSTRAN = 10;
const ODSPODU = 'max(10px, calc(env(safe-area-inset-bottom, 0px) - 12px))';

/** Kolik místa nechat pod obsahem, ať konec stránky nekončí pod lištou. */
export const REZERVA_LISTY = `calc(${ODSPODU} + ${VYSKA + 12}px)`;

/**
 * Rozměry a poloha pásu — pro další plovoucí kapsle, aby s lištou seděly.
 * Používá je psací pole v chatu; bez sdílených hodnot se rozešly o pár pixelů
 * a dvě kapsle nad sebou pak vypadaly, že tam každá patří odjinud.
 */
export const KAPSLE_LISTY = {
    odstranBocni: ODSTRAN,
    odsazeniZdola: ODSPODU,
    vyska: VYSKA,
    zaobleni: VYSKA / 2,
};

const ORANZ = '#FF8A00';
const PRAH_GESTA = 10;   // px, po kterých se tah počítá za vodorovný/svislý
const DLOUHY_STISK_MS = 450;  // podržení středu, po kterém se otevřou Moduly

// Jemné cvaknutí při přejetí na další sekci — převzato z Palce.
// iOS web vibrace nemá; od iOS 18 cvakne přepínač <input switch>.
function cvak() {
    try {
        if (navigator.vibrate) { navigator.vibrate(8); return; }
        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.setAttribute('switch', '');
        label.appendChild(input);
        label.style.display = 'none';
        document.body.appendChild(label);
        label.click();
        label.remove();
    } catch { /* bez haptiky */ }
}

export default function MobilniLista({
    currentPage, onNavigate, onOpenMenu, menuOtevreno = false,
    maModuly = true,       // má uživatel co otevřít v menu modulů?
    onZnalosti,            // otevře seznam Znalostí (sheet, ne modul)
    znalostiOtevreno = false,
    modul,                 // { label, Icon, color, colorBg } — kde jsem
    sekce = null,          // { sekce: [...], aktivni, onVyber } z useSekce
    pocty = {},            // { zpravy, notifikace, moduly }
    isDark = true,
}) {
    const [otevreno, setOtevreno] = useState(false);
    const [podPrstem, setPodPrstem] = useState(null);
    const polozky = useRef({});
    const gesto = useRef(null);
    const dlouhyStisk = useRef(null);     // časovač dlouhého stisku středu → Moduly
    const [stisknuto, setStisknuto] = useState(false);
    const navRef = useRef(null);
    const slotRefs = useRef({});
    const [indikator, setIndikator] = useState(null);   // { left, width } aktivního slotu
    const schovana = useSchovaniPriListovani({ vypnuto: otevreno || menuOtevreno, klic: `${currentPage}|${sekce?.aktivni ?? ''}` });

    // Sekce označená `skryta` se do sloupce nekreslí — má v liště vlastní
    // slot a byla by tam podruhé. Ze seznamu ale zmizet nesmí: střed v něm
    // hledá název otevřené sekce a bez záznamu spadl na název stránky, takže
    // v Notifikacích tvrdil „Přehled".
    const sekceVeSloupci = sekce?.sekce?.filter(s => !s.skryta) ?? [];
    const maSekce = sekceVeSloupci.length > 0;
    const zavri = () => { setOtevreno(false); setPodPrstem(null); };
    const vyber = (id) => {
        zavri();
        if (id !== sekce?.aktivni) sekce?.onVyber?.(id);
    };

    // Stránka se přepnula → zavřít sloupec, ať nevisí nad cizím obsahem.
    useEffect(() => { zavri(); }, [currentPage]);


    useEffect(() => {
        if (!otevreno) return;
        const klavesa = (e) => { if (e.key === 'Escape') zavri(); };
        document.addEventListener('keydown', klavesa);
        return () => document.removeEventListener('keydown', klavesa);
    }, [otevreno]);

    // ── Gesto na středu: podržet a přejet ─────────────────────────────
    const podPrstemNa = (x, y) => {
        for (const [id, el] of Object.entries(polozky.current)) {
            if (!el) continue;
            const r = el.getBoundingClientRect();
            if (x >= r.left - 16 && x <= r.right + 16 && y >= r.top - 6 && y <= r.bottom + 6) return id;
        }
        return null;
    };

    const zrusDlouhyStisk = () => { clearTimeout(dlouhyStisk.current); dlouhyStisk.current = null; };
    const stredDolu = (e) => {
        if (!maSekce && !maModuly) return;
        gesto.current = { x0: e.clientX, y0: e.clientY, tahem: false, dlouhy: false };
        e.currentTarget.setPointerCapture?.(e.pointerId);
        setStisknuto(true);
        // Podržení bez pohybu = Moduly. Pohyb nahoru (sekce) časovač zruší.
        if (maModuly) {
            zrusDlouhyStisk();
            dlouhyStisk.current = setTimeout(() => {
                dlouhyStisk.current = null;
                if (!gesto.current || gesto.current.tahem) return;
                gesto.current.dlouhy = true;
                setStisknuto(false);
                cvak();
                zavri();
                if (!menuOtevreno) onOpenMenu?.();
            }, DLOUHY_STISK_MS);
        }
    };
    const stredPohyb = (e) => {
        if (!gesto.current || gesto.current.dlouhy) return;
        const dy = gesto.current.y0 - e.clientY;
        const posun = Math.hypot(e.clientX - gesto.current.x0, dy);
        if (posun > PRAH_GESTA) zrusDlouhyStisk();
        if (!maSekce) return;
        if (!gesto.current.tahem) {
            if (dy < PRAH_GESTA) return;
            gesto.current.tahem = true;
            setOtevreno(true);
            return;                        // sloupec se teprve kreslí, položky ještě nemají rozměr
        }
        const nad = podPrstemNa(e.clientX, e.clientY);
        setPodPrstem(prev => { if (nad && nad !== prev) cvak(); return nad; });
    };
    const stredNahoru = () => {
        const g = gesto.current;
        gesto.current = null;
        zrusDlouhyStisk();
        setStisknuto(false);
        if (!g || g.dlouhy) return;        // dlouhý stisk už otevřel Moduly
        if (!maSekce) return;              // bez sekcí je krátké klepnutí jen ukazatel
        if (!g.tahem) {                    // jen klepnutí → sloupec zůstane otevřený
            setOtevreno(o => !o);
            setPodPrstem(null);
            return;
        }
        if (podPrstem) vyber(podPrstem);   // puštění nad položkou přepne
        else zavri();                      // puštění mimo nedělá nic
    };


    // Mléčné sklo: matná deska, která podklad **rozptýlí**, ne prosvítí.
    // Zkoušel jsem opak — hodně průhledné a syté, s 1px rámečkem kolem pásu
    // i kolem aktivního slotu — a bylo to špatně: obsah se pod lištou dal číst
    // a ostré linky rozsekaly měkký tvar na obtažené destičky.
    //
    // Na tmavém podkladu je závoj **světlý**, ne tmavý: lišta má nad stránkou
    // plavat, a plave to, co je světlejší než pozadí. Velké rozostření dělá
    // mléčnost,  nechá barvy jen prosvítat náladou.
    // Žádné rámečky — tvar drží stín a odstín, ne obtažení.
    // Průhlednost je schválně **stejná jako u dlaždic modulů** (`sklo`
    // v MobilniMenu) — lišta a menu jsou dvě plochy téhož systému a neměly by
    // vypadat každá z jiného materiálu. Mléčnost nedělá krytí, ale rozostření;
    // linka kolem by z toho zase udělala obtaženou destičku, takže tvar drží
    // světlo a stín.
    // Materiál je sdílený s menu a zásuvkou (`ROZOSTRENI_NAVIGACE`) — jsou to
    // plochy téhož systému a nemají vypadat každá z jiného skla.
    const rozostreni = ROZOSTRENI_NAVIGACE;
    const sklo = isDark
        ? {
            background: 'rgba(255,255,255,0.07)',
            boxShadow: '0 12px 36px rgba(0,0,0,0.44), inset 0 1px 0 rgba(255,255,255,0.30), inset 0 -8px 20px rgba(255,255,255,0.05)',
        }
        : {
            background: 'rgba(255,255,255,0.55)',
            boxShadow: '0 12px 32px rgba(30,30,50,0.18), inset 0 1px 0 rgba(255,255,255,0.95), inset 0 -8px 20px rgba(255,255,255,0.35)',
        };
    // Vyzdvižená plocha uvnitř pásu (aktivní slot, střed) — o stupeň
    // světlejší závoj, zase bez linky.
    const vyzdvih = isDark ? 'rgba(255,255,255,0.13)' : 'rgba(255,255,255,0.74)';
    const tlumena = isDark ? 'rgba(255,255,255,0.72)' : 'rgba(30,30,50,0.62)';

    // Odznak sedí nad ikonou, ne na ní: posazený k jejímu pravému hornímu
    // rohu. Kreslí ho sdílená komponenta — v mřížce modulů byl dřív jiný
    // (20 px, jiný prstenec), takže totéž číslo vypadalo na dvou místech
    // jinak.
    const odznak = (pocet) => (
        <Odznak pocet={pocet} isDark={isDark}
            style={{ position: 'absolute', top: 3, left: 'calc(50% + 3px)' }} />
    );

    // Sloty se dělí o místo flexem, ne procentní šířkou. S pevnými procenty
    // (4×19 % + 26 % = 102 %) se pás přetekl, `space-around` neměl co rozdat
    // a sloty se dotýkaly bez mezery.
    // Sloty jsou **akce**, ne stav. „Domů" je návrat na dnešní směnu,
    // „Notifikace" zkratka na notifikace — ani jedno neříká, kde jsem. To říká
    // střed, a ten je jediný zvýrazněný. Jediná výjimka je „Akce": když je
    // sloupec otevřený, musí být poznat, komu patří — a to barvou, ne plochou.
    const slot = ({ id, Icon, label, onClick, pocet = 0, zvyrazneno = false }) => (
        <button key={id} type="button" onClick={onClick} ref={(el) => { slotRefs.current[id] = el; }}
            aria-label={pocet > 0 ? `${label}, ${pocet} nových` : label}
            aria-current={['prehled', 'zpravy', 'znalosti'].includes(id) && zvyrazneno ? 'page' : undefined}
            style={{
                // Rovný díl zbylého místa, ne šířka podle textu: poloha slotů
                // se pak nehne, ať je uprostřed „Suroviny" nebo „Mapování Objedname".
                position: 'relative', zIndex: 1, flex: '1 1 0', minWidth: 0, height: 54, borderRadius: 999,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
                // Pozadí aktivního slotu kreslí jezdící kapsle (`indikator`) pod sloty.
                background: 'none',
                border: 'none', cursor: 'pointer',
                font: 'inherit', WebkitTapHighlightColor: 'transparent',
            }}>
            {/* Aktivní ikona je vyplněná, nečinná obrysová (jako Facebook a iOS)
                a při rozsvícení lehce poskočí. Obrys a vnitřní kresba (záložka
                knihy, dveře domu) jsou ve výplni **průhledný výřez** — prosvítá
                jimi kapsle. Plná výplň i čar slila knihu do bílého obdélníku
                a čáry v barvě podkladu působily jako černý obrys. */}
            {zvyrazneno
                ? <IkonaVyrez Icon={Icon} />
                : <Icon size={24} strokeWidth={2} style={{ color: tlumena, transition: 'color 0.2s ease' }} />}
            <span style={{
                fontSize: 9.5, fontWeight: zvyrazneno ? 700 : 600,
                color: zvyrazneno ? 'var(--text-primary)' : tlumena,
                maxWidth: '100%', padding: '0 2px',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{label}</span>
            {odznak(pocet)}
        </button>
    );

    // Co je v kolečku: ikona otevřené sekce, a když stránka sekce nemá, ikona
    // modulu. Sekce je jemnější údaj — v Receptáři se mezi Surovinami
    // a Produkty přepíná pořád, kdežto že jsem v Receptáři, je vidět z obsahu.
    const aktivniSekce = sekce?.sekce?.find(s => s.id === sekce.aktivni);
    const StredIkona = aktivniSekce?.Icon || modul?.Icon;
    const nazevStredu = aktivniSekce?.label ?? modul?.label ?? '';
    const ModulIkona = modul?.Icon;
    // Barva modulu pro popředí: ve světlém motivu ztmavený tón téhož odstínu,
    // jinak by ikona na mléčném skle nebyla vidět (viz `barvaSekce`).
    const barvaModulu = (isDark ? modul?.color : (modul?.colorSvetly || modul?.color)) || ORANZ;
    // Kde zrovna stojím — slot, na kterém se zůstává, to ukáže kapslí.
    // Domů nesmí svítit, když otevřená sekce Přehledu má v liště vlastní slot:
    // je to pořád Přehled, ale člověk je v notifikacích nebo na profilu.
    const sekceSeSlotem = ['notifikace'];
    const jsemDoma = currentPage === 'prehled' && !sekceSeSlotem.includes(sekce?.aktivni);
    const jsemVeZpravach = currentPage === 'zpravy';
    const jsemVeZnalostech = znalostiOtevreno;
    // Notifikace bydlí na dvou místech: samostatná stránka Aktivita a sekce
    // Přehledu. Obojí je totéž místo, takže se slot rozsvítí v obou případech.
    const jsemVNotifikacich = currentPage === 'aktivita'
        || (currentPage === 'prehled' && sekce?.aktivni === 'notifikace');
    const aktivniSlot = jsemDoma ? 'prehled' : jsemVNotifikacich ? 'aktivita'
        : jsemVeZpravach ? 'zpravy' : jsemVeZnalostech ? 'znalosti' : null;

    // Jezdící zvýraznění: poloha aktivního slotu v pásu. Při změně stránky se
    // kapsle přesune (CSS přechod), bez aktivního slotu zhasne na místě.
    useLayoutEffect(() => {
        const zmer = () => {
            const el = aktivniSlot ? slotRefs.current[aktivniSlot] : null;
            if (!el) { setIndikator(i => (i ? { ...i, skryty: true } : null)); return; }
            setIndikator({ left: el.offsetLeft, width: el.offsetWidth, skryty: false });
        };
        zmer();
        window.addEventListener('resize', zmer);
        return () => window.removeEventListener('resize', zmer);
    }, [aktivniSlot]);

    return createPortal((
        <>
            <style>{`
                @keyframes lista-polozka-in {
                    from { opacity: 0; transform: translateY(12px) scale(0.94); }
                    to   { opacity: 1; transform: none; }
                }
                @keyframes lista-clona-in { from { opacity: 0; } to { opacity: 1; } }
                @keyframes lista-ikona-pop {
                    0%   { transform: scale(0.82); }
                    60%  { transform: scale(1.12); }
                    100% { transform: scale(1); }
                }
                @media (prefers-reduced-motion: reduce) {
                    .lista-polozka { animation: none !important; }
                    .lista-clona { animation: none !important; }
                    .lista-indikator { transition: none !important; }
                    nav[aria-label="Hlavní navigace"] svg { animation: none !important; }
                }
            `}</style>

            {otevreno && (
                <div className="lista-clona" aria-hidden="true" onClick={zavri}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 58,
                        background: isDark ? 'rgba(0,0,0,0.38)' : 'rgba(30,30,50,0.16)',
                        backdropFilter: ROZOSTRENI_CLONY, WebkitBackdropFilter: ROZOSTRENI_CLONY,
                        animation: 'lista-clona-in 0.15s ease-out',
                    }} />
            )}

            {/* Sloupec sekcí nad středem — první sekce nejblíž prstu */}
            {otevreno && maSekce && (
                <div role="menu" aria-label="Sekce stránky"
                    style={{
                        position: 'fixed', zIndex: 60,
                        left: '50%', transform: 'translateX(-50%)',
                        bottom: `calc(${ODSPODU} + ${VYSKA + 14}px)`,
                        display: 'flex', flexDirection: 'column-reverse', alignItems: 'center', gap: 7,
                        maxWidth: 'calc(100vw - 32px)',
                        // Stránka s hodně sekcemi (Konfigurace jich má 14) by
                        // sloupcem vylezla nad horní hranu — pak se roluje.
                        maxHeight: 'calc(100dvh - 210px)', overflowY: 'auto',
                    }}>
                    {sekceVeSloupci.map((s, i) => {
                        const pod = podPrstem === s.id;
                        const akt = sekce.aktivni === s.id;
                        const SIkona = s.Icon;
                        return (
                            <button key={s.id} ref={(el) => { polozky.current[s.id] = el; }}
                                role="menuitem" type="button"
                                onClick={() => vyber(s.id)}
                                className="lista-polozka"
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    minHeight: 40, padding: '0 18px', borderRadius: 999,
                                    whiteSpace: 'nowrap', cursor: 'pointer', font: 'inherit',
                                    fontSize: 13, fontWeight: 600,
                                    border: 'none',
                                    ...sklo,
                                    backdropFilter: rozostreni, WebkitBackdropFilter: rozostreni,
                                    // Aktivní položka se pozná plochou, ne barvou
                                    // textu — oranžová na světlém skle není vidět.
                                    color: 'var(--text-primary)',
                                    fontWeight: akt ? 700 : 600,
                                    ...(akt ? { background: vyzdvih } : null),
                                    transform: pod ? 'scale(1.07)' : 'none',
                                    transition: 'transform 0.14s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                    animation: `lista-polozka-in 0.18s ease-out ${i * 0.025}s both`,
                                }}>
                                {/* Sekce může nést profilovou fotku místo ikony
                                    (Můj profil) — mezi pěti stejnými glyfy se
                                    jinak hledá, a je to jediná sekce, která
                                    patří člověku, ne práci. */}
                                {s.avatar
                                    ? <img src={s.avatar} alt="" style={{
                                        width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                                        objectFit: "cover",
                                    }} />
                                    : SIkona && <SIkona size={15} style={{ color: akt ? ORANZ : "var(--text-muted)" }} />}
                                {s.label}
                            </button>
                        );
                    })}
                </div>
            )}

            <nav aria-label="Hlavní navigace" ref={navRef} aria-hidden={schovana || undefined}
                style={{
                    position: 'fixed', zIndex: 59,
                    left: ODSTRAN, right: ODSTRAN, bottom: ODSPODU,
                    // Schování: celá kapsle i s odstupem pod spodní hranu
                    transform: schovana ? `translateY(calc(100% + ${ODSPODU} + 8px))` : 'none',
                    opacity: schovana ? 0 : 1,
                    pointerEvents: schovana ? 'none' : undefined,
                    transition: 'transform 0.26s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.2s ease',
                    height: VYSKA, borderRadius: VYSKA / 2,
                    display: 'flex', alignItems: 'center', gap: 2,
                    padding: '0 5px',
                    border: 'none',
                    ...sklo,
                    backdropFilter: rozostreni, WebkitBackdropFilter: rozostreni,
                }}>
                {/* Jezdící kapsle pod aktivním slotem (viz komentář u komponenty) */}
                {indikator && (
                    <span aria-hidden="true" className="lista-indikator" style={{
                        position: 'absolute', top: (VYSKA - 54) / 2, left: 0, height: 54,
                        width: indikator.width, borderRadius: 999, background: vyzdvih,
                        transform: `translateX(${indikator.left}px)`,
                        opacity: indikator.skryty ? 0 : 1,
                        transition: 'transform 0.34s cubic-bezier(0.34, 1.28, 0.64, 1), width 0.34s ease, opacity 0.2s ease',
                        pointerEvents: 'none',
                    }} />
                )}
                {/* 3) Domů je vždycky návrat na začátek — i když Přehled
                    zrovna otevřený je. Klepnutí proto nese sekci, ne jen stránku. */}
                {slot({
                    id: 'prehled', Icon: Home, label: 'Domů',
                    onClick: () => onNavigate('prehled', { tab: 'moje-smena' }),
                    zvyrazneno: jsemDoma,
                })}
                {slot({
                    id: 'aktivita', Icon: Bell, label: 'Notifikace',
                    onClick: () => onNavigate('aktivita'), pocet: pocty.notifikace || 0,
                    zvyrazneno: jsemVNotifikacich,
                })}
                {/* Střed — kde jsem. Není to cíl navigace jako ostatní sloty, ale
                    ukazatel, takže se odlišuje **barvou a velikostí ikony**, ne
                    výškou. Zkoušel to vystupovat nad pás a bylo to špatně: dole
                    zůstaly zaoblené rohy ležet na ploše lišty a četlo se to jako
                    dvě překrývající se desky. Uvnitř pásu je čistý tvar a velká
                    ikona v barvě modulu stejně padne do oka první. */}
                <button type="button" data-stred=""
                    onPointerDown={stredDolu} onPointerMove={stredPohyb}
                    onPointerUp={stredNahoru} onPointerCancel={() => { gesto.current = null; zrusDlouhyStisk(); setStisknuto(false); zavri(); }}
                    onContextMenu={(e) => e.preventDefault()}
                    aria-label={[nazevStredu, maSekce ? 'klepnutím sekce' : null, maModuly ? 'podržením moduly' : null].filter(Boolean).join(' — ')}
                    aria-expanded={maSekce ? otevreno : undefined}
                    disabled={!maSekce && !maModuly}
                    style={{
                        // Kolečko: pevných 52 px, ať zbytek pásu patří slotům.
                        position: 'relative', zIndex: 1, flex: '0 0 52px', height: 52, borderRadius: '50%',
                        // Podržení: kolečko se zmáčkne, ať je cítit, že se něco chystá
                        transform: stisknuto && maModuly ? 'scale(0.9)' : 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: vyzdvih,
                        border: 'none',
                        cursor: maSekce || maModuly ? 'pointer' : 'default', font: 'inherit',
                        touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none',
                        WebkitTouchCallout: 'none', WebkitTapHighlightColor: 'transparent',
                        transition: `background 0.15s ease, transform ${stisknuto ? DLOUHY_STISK_MS : 150}ms ease`,
                    }}>
                    {StredIkona && (
                        <StredIkona size={24} strokeWidth={2.2}
                            style={{ flexShrink: 0, color: barvaModulu }} />
                    )}
                    {/* Že se dá vysunout sloupec, říká čárka nad kolečkem —
                        gesto podržet-a-přejet by jinak nikdo nehledal. */}
                    {maSekce && (
                        <span aria-hidden="true" style={{
                            position: 'absolute', top: 5, left: '50%', transform: 'translateX(-50%)',
                            width: 14, height: 2, borderRadius: 1,
                            background: barvaModulu, opacity: otevreno ? 0.9 : 0.45,
                            transition: 'opacity 0.15s ease',
                        }} />
                    )}
                </button>

                {slot({
                    id: 'zpravy', Icon: MessageCircle, label: 'Zprávy',
                    // Vždy na seznam konverzací — i z otevřené konverzace
                    // nebo jiné záložky Zpráv (Nástěnka, Úkoly…).
                    onClick: () => (currentPage === 'zpravy'
                        ? window.dispatchEvent(new Event('zpravy-seznam'))
                        : onNavigate('zpravy', { tab: 'zpravy', konverzaceId: null })),
                    pocet: pocty.zpravy || 0,
                    zvyrazneno: jsemVeZpravach,
                })}
                {/* Poslední slot jsou Znalosti — pro všechny (stránka). Moduly
                    jsou za dlouhým stiskem středu (viz komentář u komponenty). */}
                {slot({
                    id: 'znalosti', Icon: BookMarked, label: 'Znalosti',
                    onClick: onZnalosti, pocet: pocty.znalosti || 0,
                    zvyrazneno: jsemVeZnalostech,
                })}
            </nav>
        </>
    ), document.body);
}

/**
 * Vyplněná ikona s průhledným výřezem — tvar se vyplní barvou textu a čáry
 * ikony (obrys i vnitřní kresba) se z výplně vyříznou maskou. Skrz výřez
 * prosvítá, co je pod ikonou (kapsle, sklo), takže kresba zůstane čitelná
 * v obou motivech bez barvy „podkladu" natvrdo.
 *
 * Lucide ikona je vnořené `<svg>`; maska ji vykreslí černě (= průhledné)
 * přes bílý obdélník (= viditelné).
 */
function IkonaVyrez({ Icon, size = 24 }) {
    const id = `vyrez-${useId().replace(/:/g, '')}`;
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true"
            style={{ color: 'var(--text-primary)', animation: 'lista-ikona-pop 0.36s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
            <defs>
                <mask id={id} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
                    <rect width="24" height="24" fill="#fff" />
                    <Icon size={24} strokeWidth={3.4} color="#000" fill="none" />
                </mask>
            </defs>
            <g mask={`url(#${id})`}>
                <Icon size={24} strokeWidth={2} fill="currentColor" />
            </g>
            {/* Tenký vnější obrys: drží ikoně původní velikost — bez něj
                zůstala jen vyplněná plocha uvnitř výřezu a ikona vypadala
                jako zmenšenina. Mezi obrysem a výplní je průhledná spára. */}
            <Icon size={24} strokeWidth={1.1} fill="none" />
        </svg>
    );
}

/**
 * Schování lišty při listování — tak, aby se **vždycky vrátila**.
 *
 * Dřív (do 2026-09) se lišta schovávala taky, ale „schováno" si pamatovala
 * lišta a vrátit ji uměl jen scroll nahoru. Když prvek, který ji schoval,
 * zmizel (mobil má jeden sloupec: ze seznamu se stane detail) a nový neměl
 * co scrollovat — krátká záložka, prázdný seznam — lišta zůstala dole navždy
 * i s celou navigací. Proto bylo schovávání vypnuté.
 *
 * Teď je schování jen dočasné a návrat nezávisí na tom, kdo scrolloval:
 *  - schová se při listování dolů (kterýkoli prvek, zachytává se `scroll`
 *    v capture fázi — stránky scrollují v kontejnerech, ne v okně)
 *  - vrátí se při listování nahoru, u horní hrany obsahu,
 *  - **vždy po chvilce bez scrollu** (`NAVRAT_PO_MS`) — tohle je pojistka:
 *    když scroll skončí, lišta je zpátky, ať se mezitím stalo cokoli,
 *  - při přepnutí stránky / sekce (`klic`), otočení a návratu do aplikace.
 * `REZERVA_LISTY` pod obsahem zůstává, takže konec stránky nikdy nezmizí pod lištou.
 */
const NAVRAT_PO_MS = 1100;
const PRAH_SCHOVANI = 12;    // px dolů, než se lišta schová (ne při drobném posunu)
const PRAH_NAVRATU = 6;      // px nahoru, kdy se vrátí hned

function useSchovaniPriListovani({ vypnuto, klic }) {
    const [schovana, setSchovana] = useState(false);
    const posledni = useRef(new WeakMap());
    const casovac = useRef(null);
    const vypnutoRef = useRef(vypnuto);
    vypnutoRef.current = vypnuto;

    // Přepnutí stránky / sekce, otevřený sloupec sekcí nebo menu → lišta hned zpátky
    useEffect(() => { setSchovana(false); }, [klic, vypnuto]);

    useEffect(() => {
        const ukazat = () => setSchovana(false);
        const naScroll = (e) => {
            const el = e.target === document ? document.scrollingElement : e.target;
            if (!el || typeof el.scrollTop !== 'number') return;
            const top = el.scrollTop;
            const predtim = posledni.current.get(el);
            posledni.current.set(el, top);
            clearTimeout(casovac.current);
            casovac.current = setTimeout(ukazat, NAVRAT_PO_MS);
            if (vypnutoRef.current || predtim === undefined) return;
            const posun = top - predtim;
            if (top <= 8 || posun <= -PRAH_NAVRATU) setSchovana(false);
            else if (posun >= PRAH_SCHOVANI) setSchovana(true);
        };
        const naViditelnost = () => { if (document.visibilityState === 'visible') ukazat(); };
        // Jen změna šířky (otočení) — iOS Safari posílá `resize` i když při
        // scrollu schovává adresní řádek, a lišta by se pak vracela uprostřed listování.
        let sirka = window.innerWidth;
        const naResize = () => { if (window.innerWidth !== sirka) { sirka = window.innerWidth; ukazat(); } };
        document.addEventListener('scroll', naScroll, { capture: true, passive: true });
        window.addEventListener('resize', naResize);
        window.addEventListener('orientationchange', ukazat);
        document.addEventListener('visibilitychange', naViditelnost);
        return () => {
            clearTimeout(casovac.current);
            document.removeEventListener('scroll', naScroll, { capture: true });
            window.removeEventListener('resize', naResize);
            window.removeEventListener('orientationchange', ukazat);
            document.removeEventListener('visibilitychange', naViditelnost);
        };
    }, []);

    return schovana;
}
